import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import useModalStore from '@/stores/modalStore'
import { ArrowRight, MessageSquare } from 'lucide-react'
import { getSiteSettings, SiteSettings } from '@/services/content'
import useLanguageStore from '@/stores/languageStore'
import { cn } from '@/lib/utils'
import { DashboardMockup } from '@/components/landing/DashboardMockup'

export const HeroSection = () => {
  const { openModal } = useModalStore()
  const { currentLocale } = useLanguageStore()
  const [settings, setSettings] = useState<SiteSettings | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchSettings = async () => {
      setLoading(true)
      const { data } = await getSiteSettings(currentLocale)
      if (data) setSettings(data)
      setLoading(false)
    }
    fetchSettings()
  }, [currentLocale])

  // Fallback default values if DB is empty or fails
  const heroTitle =
    settings?.hero_title ||
    (currentLocale === 'pt'
      ? 'Criamos aplicações à medida do teu negócio.'
      : 'We build custom apps for your business.')
  const heroDesc =
    settings?.hero_description ||
    (currentLocale === 'pt'
      ? 'Transformamos folhas de Excel e processos manuais em aplicações digitais com automação e inteligência artificial.'
      : 'We transform Excel sheets and manual processes into digital applications with automation and AI.')
  const ctaText =
    settings?.hero_cta_text ||
    (currentLocale === 'pt' ? 'Pedir demonstração' : 'Request Demo')
  const contactEmail = settings?.contact_email || 'geral@nucleodigital.app'

  // New logic: hero_image_url is background, dashboard_image_url is the mockup
  const bgImage = settings?.hero_image_url
  const dashboardImage = settings?.dashboard_image_url

  return (
    <section className="relative min-h-screen flex items-center justify-center pt-24 pb-16 overflow-hidden bg-gradient-to-b from-white to-slate-50">
      {/* Background Image & Overlay */}
      {bgImage ? (
        <>
          <div className="absolute inset-0 z-0">
            <img
              src={bgImage}
              alt="Background"
              className="w-full h-full object-cover"
              loading="eager" // Priority loading for Hero
            />
          </div>
          {/* Visual Overlay for Readability */}
          <div className="absolute inset-0 z-0 bg-white/90 backdrop-blur-[2px]" />
        </>
      ) : (
        /* Default Background Decor if no image */
        <div className="absolute inset-0 z-0 overflow-hidden opacity-30 pointer-events-none">
          <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] bg-indigo-100 rounded-full blur-3xl mix-blend-multiply filter opacity-70 animate-float" />
          <div className="absolute bottom-[-10%] left-[-10%] w-[600px] h-[600px] bg-blue-50 rounded-full blur-3xl mix-blend-multiply filter opacity-70 animate-float [animation-delay:2s]" />
        </div>
      )}

      <div className="container relative z-10 px-4 md:px-6 text-center max-w-6xl mx-auto">
        <div className="space-y-8 animate-fade-in-up max-w-4xl mx-auto">
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold tracking-tight text-slate-900 leading-[1.1] drop-shadow-sm">
            {loading ? (
              <span className="opacity-0">Loading...</span>
            ) : (
              heroTitle
            )}
          </h1>

          <div className="text-lg md:text-xl text-slate-600 max-w-2xl mx-auto leading-relaxed prose prose-slate prose-lg prose-p:leading-relaxed prose-a:text-primary hover:prose-a:text-primary/80">
            {loading ? (
              <span className="opacity-0">Loading...</span>
            ) : (
              <div dangerouslySetInnerHTML={{ __html: heroDesc }} />
            )}
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-6">
            <Button
              size="lg"
              className="h-14 px-8 text-lg w-full sm:w-auto shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5 font-semibold"
              onClick={openModal}
            >
              {ctaText}
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button
              variant="outline"
              size="lg"
              className="h-14 px-8 text-lg w-full sm:w-auto bg-white/80 hover:bg-white backdrop-blur-sm"
              onClick={() => window.open(`mailto:${contactEmail}`)}
            >
              {currentLocale === 'pt'
                ? 'Falar com um especialista'
                : 'Talk to an expert'}
              <MessageSquare className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>

        {/* Dashboard Mockup Component */}
        <div className="mt-20 relative mx-auto max-w-5xl animate-fade-in [animation-delay:500ms]">
          <DashboardMockup
            imageUrl={dashboardImage}
            className="shadow-2xl ring-1 ring-slate-900/5"
            readOnly
          />
        </div>
      </div>
    </section>
  )
}
